// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Ricardo Sarmento - https://linx.pt

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import AppLayout from '../components/AppLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuthStore } from '../stores/authStore';

function AgendaPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [iframeInput, setIframeInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const { data: tenantData, isLoading, error, refetch } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: async () => {
      const response = await api.get('/tenants/me');
      return response.data.data;
    }
  });

  const isAdmin = user?.roles?.some(role => 
    ['tenant_admin', 'admin', 'tannat'].includes(role.slug) || 
    role.permissions?.includes('level:full_access')
  );

  if (isLoading) {
    return (
      <AppLayout hideLayout={true}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: 'var(--background)' }}>
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout hideLayout={true}>
        <div style={{ padding: '2rem', textAlign: 'center', backgroundColor: 'var(--background)', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <h2 style={{ color: 'var(--text-primary)' }}>Erro ao carregar configurações</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>{error.message || 'Erro desconhecido'}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Recarregar
          </button>
        </div>
      </AppLayout>
    );
  }

  const calendarIframe = tenantData?.settings?.calendar_iframe || '';

  const handleOpenModal = () => {
    setIframeInput(calendarIframe);
    setErrorMsg('');
    setSuccessMsg('');
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const response = await api.put('/tenants/me', {
        settings: {
          calendar_iframe: iframeInput
        }
      });

      if (response.data.success) {
        setSuccessMsg('Agenda configurada com sucesso!');
        await refetch();
        setTimeout(() => {
          setIsModalOpen(false);
        }, 1000);
      } else {
        setErrorMsg('Não foi possível guardar as alterações.');
      }
    } catch (err) {
      console.error('Erro ao guardar agenda:', err);
      setErrorMsg(err.response?.data?.message || 'Erro ao atualizar a agenda.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout hideLayout={true}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: 'var(--background)' }}>
        
        {/* Header/Topbar */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          padding: '1rem 1.5rem', 
          backgroundColor: 'var(--surface)', 
          borderBottom: '1px solid var(--border)',
          gap: '1rem'
        }}>
          <button 
            className="btn btn-ghost" 
            onClick={() => navigate('/dashboard')}
            style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            title="Voltar ao Dashboard"
          >
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0, color: 'var(--text-primary)' }}>
            Agenda da Instituição
          </h1>
          {isAdmin && (
            <div style={{ marginLeft: 'auto' }}>
              <button 
                className="btn btn-outline btn-sm" 
                onClick={handleOpenModal}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>settings</span>
                Configurar Agenda
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {calendarIframe ? (
            <div 
              style={{ width: '100%', height: '100%' }}
              className="calendar-wrapper-fullscreen"
              dangerouslySetInnerHTML={{ __html: calendarIframe.replace(/width="[^"]*"/, 'width="100%"').replace(/height="[^"]*"/, 'height="100%"') }}
            />
          ) : (
            <div style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-muted)'
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: '64px', marginBottom: '1rem' }}>
                calendar_month
              </span>
              <h2 style={{ fontSize: '1.5rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                Agenda não configurada
              </h2>
              <p style={{ maxWidth: '400px', textAlign: 'center', marginBottom: '2rem' }}>
                O código de incorporação (iframe) do Google Calendar não foi configurado nas definições da instituição.
              </p>
              {isAdmin ? (
                <button 
                  className="btn btn-primary" 
                  onClick={handleOpenModal}
                >
                  Configurar Agenda
                </button>
              ) : (
                <p style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>
                  Por favor, contacte o administrador para configurar a agenda.
                </p>
              )}
            </div>
          )}
        </div>
        
      </div>

      {/* Modal de Configuração */}
      {isModalOpen && (
        <div className="modal-overlay active">
          <div className="modal" style={{ background: 'var(--surface, #ffffff)', color: 'var(--text-primary, #172B4D)', maxWidth: '500px', textAlign: 'left' }}>
            <div className="modal-header">
              <h2 className="modal-title">Configurar Agenda</h2>
              <button className="modal-close" onClick={() => setIsModalOpen(false)} disabled={saving}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            
            <div className="modal-body">
              <p className="mb-4 text-muted" style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
                Insira o código de incorporação (iframe) do Google Calendar para a agenda da instituição.
              </p>
              
              <form onSubmit={handleSave}>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label className="form-label" style={{ fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>Iframe da Agenda (Google Calendar)</label>
                  <textarea
                    className="form-input"
                    value={iframeInput}
                    onChange={(e) => setIframeInput(e.target.value)}
                    placeholder="Ex: <iframe src='https://calendar.google.com/calendar/embed?src=...'></iframe>"
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border)', minHeight: '120px', resize: 'vertical' }}
                  />
                  <small className="form-hint" style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem', display: 'block' }}>
                    Pode obter este código nas definições da sua agenda do Google Calendar, na secção &quot;Integrar agenda&quot;.
                  </small>
                </div>
                
                {errorMsg && (
                  <div className="alert alert-error mb-4" style={{ marginBottom: '1rem', padding: '0.75rem', borderRadius: '6px', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger, #ef4444)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                    {errorMsg}
                  </div>
                )}
                
                {successMsg && (
                  <div className="alert alert-success mb-4" style={{ marginBottom: '1rem', padding: '0.75rem', borderRadius: '6px', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: 'var(--success, #10b981)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                    {successMsg}
                  </div>
                )}
                
                <div className="modal-footer" style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setIsModalOpen(false)}
                    disabled={saving}
                    style={{ flex: 1 }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                    style={{ flex: 1 }}
                  >
                    {saving ? 'A guardar...' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

export default AgendaPage;
